import { Slider } from "@/components/ui/slider";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { CriteriaType } from "@/types/criteria";

interface BidirectionalSliderProps {
  value: number;
  minValue: number;
  maxValue: number;
  baselineValue: number; // The 100% expected value
  criteriaType: CriteriaType;
  weight: number;
  onChange: (value: number) => void;
  disabled?: boolean;
}

export const getSliderColor = (value: number, baselineValue: number, maxValue: number, criteriaType: CriteriaType): string => {
  if (criteriaType === 'bonus') {
    // Bonus: Green gradient from 0 to max
    const percentage = maxValue > 0 ? (value / maxValue) * 100 : 0;
    if (percentage >= 80) return "bg-emerald-500";
    if (percentage >= 60) return "bg-green-500";
    if (percentage >= 40) return "bg-lime-500";
    if (percentage >= 20) return "bg-yellow-500";
    return "bg-gray-400";
  }
  
  // Standard: Color based on relation to baseline
  if (value >= baselineValue) {
    // At or above baseline - green shades
    const overPercentage = maxValue > baselineValue 
      ? ((value - baselineValue) / (maxValue - baselineValue)) * 100 
      : 100;
    if (overPercentage >= 50) return "bg-emerald-500";
    return "bg-green-500";
  } else {
    // Below baseline - yellow to red
    const underPercentage = baselineValue > 0 ? (value / baselineValue) * 100 : 0;
    if (underPercentage >= 75) return "bg-lime-500";
    if (underPercentage >= 50) return "bg-yellow-500";
    if (underPercentage >= 25) return "bg-orange-500";
    return "bg-red-500";
  }
};

export const getSliderTextColor = (value: number, baselineValue: number, maxValue: number, criteriaType: CriteriaType): string => {
  if (criteriaType === 'bonus') {
    const percentage = maxValue > 0 ? (value / maxValue) * 100 : 0;
    if (percentage >= 60) return "text-green-600";
    if (percentage >= 40) return "text-lime-600";
    if (percentage >= 20) return "text-yellow-600";
    return "text-muted-foreground";
  }
  
  if (value >= baselineValue) {
    return "text-green-600";
  } else {
    const underPercentage = baselineValue > 0 ? (value / baselineValue) * 100 : 0;
    if (underPercentage >= 75) return "text-lime-600";
    if (underPercentage >= 50) return "text-yellow-600";
    if (underPercentage >= 25) return "text-orange-600";
    return "text-red-600";
  }
};

export const getRatingLabel = (value: number, baselineValue: number, maxValue: number, criteriaType: CriteriaType): string => {
  if (criteriaType === 'bonus') {
    const percentage = maxValue > 0 ? (value / maxValue) * 100 : 0;
    if (percentage >= 80) return "Hervorragend";
    if (percentage >= 60) return "Sehr gut";
    if (percentage >= 40) return "Gut";
    if (percentage >= 20) return "Etwas geleistet";
    return "Keine Zusatzleistung";
  }
  
  if (value > baselineValue) {
    const overPercentage = maxValue > baselineValue 
      ? ((value - baselineValue) / (maxValue - baselineValue)) * 100 
      : 100;
    if (overPercentage >= 50) return "Über Erwartung";
    return "Leicht über Standard";
  } else if (value === baselineValue) {
    return "Standard erfüllt";
  } else {
    const underPercentage = baselineValue > 0 ? (value / baselineValue) * 100 : 0;
    if (underPercentage >= 75) return "Teilweise erfüllt";
    if (underPercentage >= 50) return "Verbesserungsbedarf";
    if (underPercentage >= 25) return "Mangelhaft";
    return "Nicht erfüllt";
  }
};

const getWeightLabel = (weight: number): { label: string; color: string } => {
  switch (weight) {
    case 3: return { label: "Sehr wichtig", color: "bg-red-500/20 text-red-700 border-red-500/30" };
    case 2: return { label: "Wichtig", color: "bg-orange-500/20 text-orange-700 border-orange-500/30" };
    default: return { label: "Normal", color: "bg-gray-500/20 text-gray-600 border-gray-500/30" };
  }
};

const BidirectionalSlider = ({ 
  value, 
  minValue, 
  maxValue, 
  baselineValue,
  criteriaType,
  weight,
  onChange, 
  disabled = false 
}: BidirectionalSliderProps) => {
  const colorClass = getSliderColor(value, baselineValue, maxValue, criteriaType);
  const textColorClass = getSliderTextColor(value, baselineValue, maxValue, criteriaType);
  const ratingLabel = getRatingLabel(value, baselineValue, maxValue, criteriaType);
  const weightInfo = getWeightLabel(weight);
  
  // Calculate baseline position as percentage
  const baselinePosition = maxValue > minValue 
    ? ((baselineValue - minValue) / (maxValue - minValue)) * 100 
    : 0;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <div className={cn(
            "w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-sm",
            colorClass
          )}>
            {value}
          </div>
          <div className="flex flex-col">
            <span className={cn("text-sm font-medium", textColorClass)}>
              / {maxValue} Pkt.
            </span>
            {criteriaType === 'standard' && (
              <span className="text-xs text-muted-foreground">
                Standard: {baselineValue}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className={cn("text-xs", weightInfo.color)}>
            {weightInfo.label}
          </Badge>
          <Badge variant={criteriaType === 'bonus' ? 'default' : 'secondary'} className="text-xs">
            {criteriaType === 'bonus' ? 'Bonus' : 'Standard'}
          </Badge>
        </div>
      </div>
      
      <div className="relative pt-2">
        {/* Baseline marker for standard criteria */}
        {criteriaType === 'standard' && baselineValue > minValue && baselineValue < maxValue && (
          <div 
            className="absolute top-0 w-0.5 h-2 bg-primary z-10"
            style={{ left: `${baselinePosition}%` }}
          >
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 text-[10px] text-primary font-medium whitespace-nowrap">
              100%
            </div>
          </div>
        )}
        
        <Slider
          value={[value]}
          max={maxValue}
          min={minValue}
          step={1}
          onValueChange={([newValue]) => onChange(newValue)}
          disabled={disabled}
          className={cn(
            "cursor-pointer",
            disabled && "cursor-not-allowed opacity-50"
          )}
        />
      </div>
      
      <div className="flex justify-between items-center">
        <span className="text-xs text-muted-foreground">{minValue}</span>
        <span className={cn("text-xs font-medium px-2 py-0.5 rounded-full", colorClass, "text-white")}>
          {ratingLabel}
        </span>
        <span className="text-xs text-muted-foreground">{maxValue}</span>
      </div>
      
      {/* Weighted score display */}
      {weight > 1 && (
        <div className="text-xs text-muted-foreground text-center border-t pt-2 mt-1">
          Gewichtete Punkte: <span className="font-medium text-foreground">{value * weight}</span> / {maxValue * weight}
        </div>
      )}
    </div>
  );
};

export default BidirectionalSlider;